/**
 * BESTIX v5.0 — Rewards Banner
 * Vivid Seats-style loyalty program banner
 */

import { Link } from 'react-router-dom';
import { Crown, Gift, Star, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface RewardsBannerProps {
  variant?: 'full' | 'compact' | 'card';
  className?: string;
}

const tiers = [
  { name: 'Silver', points: 0, color: 'bg-gray-400', benefits: '2% back on purchases' },
  { name: 'Gold', points: 5000, color: 'bg-[var(--color-gold)]', benefits: '5% back + priority access' },
  { name: 'Platinum', points: 15000, color: 'bg-purple-500', benefits: '10% back + concierge + VIP' },
];

export function RewardsBanner({ variant = 'full', className }: RewardsBannerProps) {
  if (variant === 'compact') {
    return (
      <div className={cn('bg-gradient-to-r from-[var(--color-navy)] to-[var(--color-navy-light)] py-3 px-4', className)}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Crown className="w-5 h-5 text-[var(--color-gold)]" />
            <span className="text-white font-medium">Join BESTIX Rewards</span>
            <span className="text-white/70 text-sm hidden sm:inline">— Earn up to 10% back on every purchase</span>
          </div>
          <Link
            to="/rewards"
            className="flex items-center gap-1 text-[var(--color-gold)] text-sm font-medium hover:text-[var(--color-gold-light)]"
          >
            Learn More
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  if (variant === 'card') {
    return (
      <div className={cn('bg-white rounded-xl shadow-md overflow-hidden', className)}>
        <div className="bg-gradient-to-r from-[var(--color-navy)] to-[var(--color-navy-light)] p-6">
          <div className="flex items-center gap-3 mb-2">
            <Crown className="w-8 h-8 text-[var(--color-gold)]" />
            <h3 className="font-playfair text-2xl text-white">BESTIX Rewards</h3>
          </div>
          <p className="text-white/70">Earn points on every purchase</p>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {tiers.map((tier) => (
              <div key={tier.name} className="flex items-center gap-3">
                <div className={cn('w-3 h-3 rounded-full', tier.color)} />
                <div className="flex-1">
                  <span className="font-medium text-[var(--color-navy)]">{tier.name}</span>
                  <span className="text-gray-400 text-sm ml-2">({tier.points.toLocaleString()}+ pts)</span>
                </div>
                <span className="text-sm text-gray-500">{tier.benefits}</span>
              </div>
            ))}
          </div>
          <Link
            to="/rewards"
            className="block w-full mt-6 py-3 text-center bg-[var(--color-gold)] text-[var(--color-navy)] rounded-full font-medium hover:bg-[var(--color-gold-light)] transition-colors"
          >
            Join Now — It's Free
          </Link>
        </div>
      </div>
    );
  }

  // Full banner
  return (
    <section className={cn('bg-gradient-to-br from-[var(--color-navy)] via-[var(--color-navy-light)] to-[var(--color-navy)]', className)}>
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left: Content */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Crown className="w-10 h-10 text-[var(--color-gold)]" />
              <span className="text-[var(--color-gold)] text-sm font-medium uppercase tracking-wide">Loyalty Program</span>
            </div>
            <h2 className="font-playfair text-4xl !text-white mb-4">
              BESTIX Rewards
            </h2>
            <p className="text-white/70 text-lg mb-8">
              Join our exclusive rewards program and earn points on every purchase. 
              Unlock VIP benefits, priority access, and up to 10% back.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/rewards"
                className="px-8 py-3 bg-[var(--color-gold)] text-[var(--color-navy)] rounded-full font-medium hover:bg-[var(--color-gold-light)] transition-colors"
              >
                Join Now — It's Free
              </Link>
              <Link
                to="/rewards"
                className="px-8 py-3 border border-white/30 text-white rounded-full font-medium hover:bg-white/10 transition-colors"
              >
                Learn More
              </Link>
            </div>
          </div>

          {/* Right: Tiers */}
          <div className="space-y-4">
            {tiers.map((tier, i) => (
              <div
                key={tier.name}
                className={cn(
                  'flex items-center gap-4 p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10',
                  i === 2 && 'ring-2 ring-[var(--color-gold)]/50'
                )}
              >
                <div className={cn('w-12 h-12 rounded-full flex items-center justify-center', tier.color)}>
                  {i === 0 && <Star className="w-6 h-6 text-white" />}
                  {i === 1 && <Gift className="w-6 h-6 text-[var(--color-navy)]" />}
                  {i === 2 && <Crown className="w-6 h-6 text-white" />}
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-white !text-white">{tier.name}</h4>
                  <p className="text-white/60 text-sm">{tier.benefits}</p>
                </div>
                <span className="text-white/40 text-sm">{tier.points.toLocaleString()}+ pts</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default RewardsBanner;
